/**
 * Voice mode state machine.
 *
 * 9 discrete states the visitor's voice session can be in. Signal-backed
 * so UI components re-render on transition without subscription
 * plumbing. The Daily session wrapper (`daily-session.ts`) drives
 * transitions in response to Daily events + chat-api SSE; UI components
 * (`components/voice/*.tsx`) read the signal and render.
 *
 * State diagram (see docs/archive/voice-widget-ux-delta.md for full spec):
 *
 *   IDLE ──click──▶ CONNECTING ──ready──▶ LISTENING ──speech──▶ THINKING
 *   ▲               │ ⤴ network err      ▲       ▲                │
 *   │               ▼                    │       │ resumes        ▼
 *   │            ERROR                   │       └ INTERRUPTED ◀ SPEAKING
 *   │                                    │
 *   │            ◀──── DEGRADED ─────────┘
 *   │                       ▲
 *   └──── close ────────────┴──────────────────────────────────────┘
 */

import { batch, computed, signal } from '@preact/signals';
import { pushMilestone } from '../data-layer';

export type VoiceStateName =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'interrupted'
  | 'degraded'
  | 'error';

export type VoiceState = {
  /** Current state. UI renders off this. */
  name: VoiceStateName;
  /** Set when name='error' — short message for the visitor. */
  errorMessage: string | null;
  /** Set when name='speaking' — bot's outgoing audio level [0..1] for the visualizer. */
  botAudioLevel: number;
  /** Visitor's mic input level [0..1] — surface in LISTENING for the visualizer. */
  micAudioLevel: number;
  /** True when the visitor has muted their own mic (Phase 1.7 long-press). */
  micMuted: boolean;
  /** Daily room URL the visitor is connected to (null when IDLE). */
  roomUrl: string | null;
  /** Server session id — the join key between audio + data planes. */
  sessionId: string | null;
};

const initial: VoiceState = {
  name: 'idle',
  errorMessage: null,
  botAudioLevel: 0,
  micAudioLevel: 0,
  micMuted: false,
  roomUrl: null,
  sessionId: null,
};

export const voiceState = signal<VoiceState>(initial);

/**
 * The bot is AUDIBLY talking right now — per-utterance. The ON edge is
 * the bot's `showpilot:bot-speaking` app message (TTS generation start);
 * the OFF edge comes from the widget's own ears (voice/playout-monitor):
 * the moment the played-out voice falls silent, which with an avatar
 * vendor in the chain is seconds after generation ends. Without ears the
 * generation edge is used for both. This is NOT `name === 'speaking'`:
 * that flips when the bot's audio TRACK starts — once, at call start —
 * and holds for the whole call, so it can't pace anything per-utterance
 * (found live 2026-08-23: the media floor watched it and never saw a
 * transition, agent + video talked over each other). The one-voice law
 * keys off THIS signal.
 */
export const botTalking = signal(false);

/** Computed: is the visitor currently in any non-idle voice mode? */
export const isVoiceActive = computed(() => voiceState.value.name !== 'idle');

/** Computed: should we treat the bot as currently producing audio? */
export const isBotSpeaking = computed(
  () => voiceState.value.name === 'speaking',
);

/**
 * Patch-style update. Callers pass only the fields they want to change;
 * everything else carries over. Using `batch` so multiple updates in
 * the same tick re-render once.
 */
export function updateVoiceState(patch: Partial<VoiceState>): void {
  batch(() => {
    voiceState.value = { ...voiceState.value, ...patch };
  });
}

/**
 * Live video-avatar track from the bot (Simli), when the agent runs one.
 * Set by `daily-session.ts` on the bot's video `track-started`/`-stopped`;
 * null when audio-only or between sessions. The video-greeter component
 * renders this MediaStream as the live talking head, overriding the
 * pre-recorded greeter for the duration of the call.
 */
export const botVideoStream = signal<MediaStream | null>(null);

/**
 * Convenience helpers for the most common transitions. Each one is a
 * focused move with a single responsibility — easier to grep for than
 * `updateVoiceState({...})` everywhere.
 */
export const voiceTransition = {
  toConnecting(roomUrl: string, sessionId: string): void {
    pushMilestone('voice_call_started', {});
    updateVoiceState({
      name: 'connecting',
      roomUrl,
      sessionId,
      errorMessage: null,
    });
  },
  toListening(): void {
    updateVoiceState({ name: 'listening', errorMessage: null, botAudioLevel: 0 });
  },
  toThinking(): void {
    updateVoiceState({ name: 'thinking', micAudioLevel: 0, botAudioLevel: 0 });
  },
  toSpeaking(): void {
    updateVoiceState({ name: 'speaking', micAudioLevel: 0 });
  },
  toInterrupted(): void {
    // INTERRUPTED is a transient — UI shows a snap of the audio bars
    // from bot→user. The session immediately moves toward LISTENING
    // as Pipecat acks the interruption. We keep INTERRUPTED briefly
    // for the visual cue.
    updateVoiceState({ name: 'interrupted', botAudioLevel: 0 });
  },
  toDegraded(): void {
    updateVoiceState({ name: 'degraded' });
  },
  toError(message: string): void {
    updateVoiceState({ name: 'error', errorMessage: message });
  },
  /** Tear down — return to the IDLE start state. */
  toIdle(): void {
    voiceState.value = initial;
    botTalking.value = false;
  },
};

/**
 * Validate that a state machine transition is legal. Used by the
 * Daily session wrapper to assert before applying; also unit-tested
 * to lock the diagram in. Returns true if `to` is reachable from
 * `from` per the spec; throws is too noisy for a state machine that
 * legitimately sees concurrent events.
 */
const ALLOWED: Record<VoiceStateName, VoiceStateName[]> = {
  idle: ['connecting'],
  connecting: ['listening', 'error', 'idle'],
  listening: ['thinking', 'speaking', 'degraded', 'error', 'idle'],
  thinking: ['speaking', 'listening', 'degraded', 'error', 'idle'],
  speaking: ['listening', 'interrupted', 'degraded', 'error', 'idle'],
  interrupted: ['listening', 'thinking', 'idle'],
  degraded: ['listening', 'speaking', 'thinking', 'error', 'idle'],
  error: ['idle', 'connecting'],
};

export function canTransition(from: VoiceStateName, to: VoiceStateName): boolean {
  if (from === to) return true;
  return ALLOWED[from]?.includes(to) ?? false;
}
